/**
 * StorageUsageCard — answers "what is eating the bucket?" for the app's
 * Firebase Storage. Click Analyze and it walks the whole bucket (recursive
 * list), then reads every object's metadata to get sizes — one metadata
 * request per object, which is why the scan is an explicit button and the
 * result is cached for the session rather than refreshed on mount.
 *
 * Views, all scoped to the current drill-down folder:
 *   - treemap of what's inside (click a folder tile to drill in)
 *   - size by file type
 *   - largest files, with open / copy URL / delete / jump-to-folder actions
 *
 * Deletes respect the same PROTECTED_ROOTS guardrails as FileManagerCard.
 * If the bucket ever grows past ~10k objects the per-object metadata reads
 * will crawl; the upgrade path is a callable that uses the Admin SDK's
 * bucket.getFiles() (sizes come back inline, 1000 per request).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  ref,
  listAll,
  getMetadata,
  getDownloadURL,
  getBytes,
  deleteObject,
  uploadBytes,
  StorageReference,
} from 'firebase/storage';
import { storage } from '../../../firebase';
import { toast } from 'react-toastify';
import { fmtBytes, fmtDate, fileIcon, isProtected } from './storageShared';

interface ScanEntry {
  path: string;
  name: string;
  size: number;
  contentType?: string;
  updated?: string;
}

interface ScanResult {
  entries: ScanEntry[];
  unreadable: number;
  at: number;
  durationMs: number;
}

interface TreeNode {
  name: string;
  path: string;
  size: number;
  count: number;
  /** null = file, Map = folder */
  children: Map<string, TreeNode> | null;
}

type Phase = 'restoring' | 'idle' | 'listing' | 'sizing' | 'done';

// Session cache — a scan survives navigating away from the page. The durable
// copy lives in the bucket itself (SCAN_FILE) and is restored on mount, so
// results persist across sessions and both users see the same picture.
let cachedScan: ScanResult | null = null;

// Saved-scan location. A Firestore doc caps at 1MB, which a full entries list
// blows straight through at this bucket's object counts; a JSON object in the
// bucket has no practical ceiling. Excluded from scans so it doesn't count itself.
const SCAN_FILE = 'system/storage-scan.json';
const MAX_SAVED_SCAN_BYTES = 100 * 1024 * 1024;
const RESTORE_TIMEOUT_MS = 12000;

const METADATA_CONCURRENCY = 24;
const LIST_CONCURRENCY = 16;
const TREEMAP_MAX_TILES = 14;
const LARGEST_COUNT = 20;
const PALETTE = [
  '#3E97FF', '#50CD89', '#F1BC00', '#F1416C', '#7239EA', '#43CED7',
  '#FF9A3E', '#8098F9', '#5CB85C', '#E06CA9', '#9AA4B2', '#C9A227',
];

const fmtAgo = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const typeCategory = (contentType: string | undefined, name: string): string => {
  const ct = contentType || '';
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (ct.startsWith('image/')) return 'Images';
  if (ct === 'application/pdf' || ext === 'pdf') return 'PDFs';
  if (ct.startsWith('video/')) return 'Video';
  if (ct.startsWith('audio/')) return 'Audio';
  if (['zip', 'gz', 'rar', '7z'].indexOf(ext) !== -1) return 'Archives';
  if (['doc', 'docx', 'xls', 'xlsx', 'csv', 'ppt', 'pptx'].indexOf(ext) !== -1) return 'Documents';
  if (ct.startsWith('text/') || ['js', 'ts', 'json', 'html', 'css', 'map', 'ttf', 'woff', 'woff2'].indexOf(ext) !== -1) return 'Code & text';
  return 'Other';
};

/** Run fn over items with bounded concurrency; onDone fires after each item. */
async function pool<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>,
  isCancelled: () => boolean
): Promise<void> {
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (!isCancelled()) {
      const i = cursor;
      cursor += 1;
      if (i >= items.length) return;
      await fn(items[i]);
    }
  };
  const workers: Promise<void>[] = [];
  for (let i = 0; i < Math.min(limit, items.length); i++) workers.push(worker());
  await Promise.all(workers);
}

const StorageUsageCard: React.FC<{ onOpenFolder?: (path: string) => void }> = ({ onOpenFolder }) => {
  const [phase, setPhase] = useState<Phase>(cachedScan ? 'done' : 'restoring');
  const [scan, setScan] = useState<ScanResult | null>(cachedScan);
  const [found, setFound] = useState(0);
  const [foldersListed, setFoldersListed] = useState(0);
  const [sized, setSized] = useState(0);
  const [drill, setDrill] = useState('');
  const [busyPath, setBusyPath] = useState('');
  const cancelRef = useRef(false);
  const treemapNodesRef = useRef<TreeNode[]>([]);

  // Restore the last saved scan from the bucket on first mount. The fetch
  // races a hard timeout so the card can never sit in "restoring" forever —
  // whatever goes wrong it falls back to the Analyze button, and the real
  // error goes to the console instead of being swallowed.
  useEffect(() => {
    if (cachedScan) return;
    let alive = true;
    let settled = false;
    const finish = (next: Phase, restored?: ScanResult) => {
      if (!alive || settled) return;
      settled = true;
      if (restored) {
        cachedScan = restored;
        setScan(restored);
      }
      setPhase(next);
    };
    const timer = window.setTimeout(() => {
      console.warn('[storage-usage] restore timed out after 12s — falling back to manual scan');
      finish('idle');
    }, RESTORE_TIMEOUT_MS);
    (async () => {
      try {
        const buf = await getBytes(ref(storage, SCAN_FILE), MAX_SAVED_SCAN_BYTES);
        const parsed = JSON.parse(new TextDecoder().decode(buf));
        if (parsed && parsed.v === 1 && Array.isArray(parsed.entries)) {
          finish('done', {
            entries: parsed.entries,
            unreadable: parsed.unreadable || 0,
            at: parsed.at || 0,
            durationMs: parsed.durationMs || 0,
          });
        } else {
          console.warn('[storage-usage] saved scan has an unexpected shape — ignoring it');
          finish('idle');
        }
      } catch (e: any) {
        // storage/object-not-found is the normal "no scan saved yet" case.
        if (e?.code !== 'storage/object-not-found') {
          console.warn('[storage-usage] restore failed:', e?.code || e?.message || e);
        }
        finish('idle');
      }
    })();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, []);

  // ---- scan ----------------------------------------------------------------

  const runScan = async () => {
    if (phase === 'listing' || phase === 'sizing' || phase === 'restoring') return;
    cancelRef.current = false;
    setPhase('listing');
    setFound(0);
    setFoldersListed(0);
    setSized(0);
    const started = Date.now();
    try {
      // Phase 1: recursive list via a bounded work queue. An unbounded
      // Promise.all here once fired a list request for every mail/<id>/
      // folder at once — thousands of parallel fetches — and Chrome refused
      // them with ERR_INSUFFICIENT_RESOURCES until the SDK's retry deadline
      // expired. Workers pull the next folder and enqueue its subfolders.
      const refs: StorageReference[] = [];
      const queue: string[] = [''];
      let foldersDone = 0;
      let unlistable = 0;
      await new Promise<void>((resolve) => {
        let active = 0;
        const pump = (): void => {
          if (cancelRef.current && active === 0) { resolve(); return; }
          while (!cancelRef.current && active < LIST_CONCURRENCY && queue.length > 0) {
            const p = queue.shift()!;
            active += 1;
            listAll(ref(storage, p))
              .then((res) => {
                refs.push(...res.items.filter((i) => i.fullPath !== SCAN_FILE));
                res.prefixes.forEach((pre) => queue.push(pre.fullPath));
              })
              .catch(() => {
                unlistable += 1;
              })
              .then(() => {
                active -= 1;
                foldersDone += 1;
                if (foldersDone % 10 === 0) {
                  setFound(refs.length);
                  setFoldersListed(foldersDone);
                }
                pump();
              });
          }
          if (active === 0 && queue.length === 0) resolve();
        };
        pump();
      });
      setFound(refs.length);
      setFoldersListed(foldersDone);
      if (cancelRef.current) { setPhase(cachedScan ? 'done' : 'idle'); return; }
      if (unlistable > 0) toast.warn(`${unlistable} folder(s) couldn't be listed — their contents are missing from the scan.`);

      // Phase 2: one getMetadata per object, bounded concurrency.
      setPhase('sizing');
      const entries: ScanEntry[] = [];
      let unreadable = 0;
      let done = 0;
      await pool(
        refs,
        METADATA_CONCURRENCY,
        async (r) => {
          try {
            const m = await getMetadata(r);
            entries.push({
              path: r.fullPath,
              name: r.name,
              size: Number(m.size) || 0,
              contentType: m.contentType || undefined,
              updated: m.updated,
            });
          } catch {
            unreadable += 1;
          }
          done += 1;
          if (done % 25 === 0 || done === refs.length) setSized(done);
        },
        () => cancelRef.current
      );
      if (cancelRef.current) { setPhase(cachedScan ? 'done' : 'idle'); return; }

      const result: ScanResult = { entries, unreadable, at: Date.now(), durationMs: Date.now() - started };
      cachedScan = result;
      setScan(result);
      setDrill('');
      setPhase('done');

      // Persist for next session / the other user. Fire-and-forget: the scan
      // is already usable on screen even if the save fails.
      persistScan(result);
    } catch (e: any) {
      toast.error(`Scan failed: ${e?.message || e}`);
      setPhase(cachedScan ? 'done' : 'idle');
    }
  };

  const cancelScan = () => {
    cancelRef.current = true;
  };

  const persistScan = (result: ScanResult) => {
    uploadBytes(
      ref(storage, SCAN_FILE),
      new Blob([JSON.stringify({ v: 1, ...result })], { type: 'application/json' })
    ).catch((e: any) => toast.warn(`Couldn't save the scan: ${e?.message || e}`));
  };

  // ---- derived data --------------------------------------------------------

  const tree = useMemo<TreeNode | null>(() => {
    if (!scan) return null;
    const root: TreeNode = { name: '', path: '', size: 0, count: 0, children: new Map() };
    for (const e of scan.entries) {
      const segs = e.path.split('/');
      root.size += e.size;
      root.count += 1;
      let node = root;
      for (let i = 0; i < segs.length; i++) {
        const isLeaf = i === segs.length - 1;
        let child = node.children!.get(segs[i]);
        if (!child) {
          child = { name: segs[i], path: segs.slice(0, i + 1).join('/'), size: 0, count: 0, children: isLeaf ? null : new Map() };
          node.children!.set(segs[i], child);
        }
        // A file and folder can share a name in GCS; promote to folder if needed.
        if (!isLeaf && !child.children) child.children = new Map();
        child.size += e.size;
        child.count += 1;
        node = child;
      }
    }
    return root;
  }, [scan]);

  const drillNode = useMemo<TreeNode | null>(() => {
    if (!tree) return null;
    if (!drill) return tree;
    let node: TreeNode | null = tree;
    for (const seg of drill.split('/')) {
      node = node?.children?.get(seg) || null;
      if (!node) break;
    }
    return node || tree;
  }, [tree, drill]);

  const treemapNodes = useMemo<TreeNode[]>(() => {
    if (!drillNode || !drillNode.children) return [];
    const kids = Array.from(drillNode.children.values())
      .filter((n) => n.size > 0)
      .sort((a, b) => b.size - a.size);
    if (kids.length <= TREEMAP_MAX_TILES) return kids;
    const top = kids.slice(0, TREEMAP_MAX_TILES);
    const rest = kids.slice(TREEMAP_MAX_TILES);
    top.push({
      name: `(${rest.length} more)`,
      path: '',
      size: rest.reduce((a, n) => a + n.size, 0),
      count: rest.reduce((a, n) => a + n.count, 0),
      children: null,
    });
    return top;
  }, [drillNode]);
  treemapNodesRef.current = treemapNodes;

  // Entries within the drill scope, for the type breakdown + largest table.
  const scopedEntries = useMemo<ScanEntry[]>(() => {
    if (!scan) return [];
    if (!drill) return scan.entries;
    const prefix = `${drill}/`;
    return scan.entries.filter((e) => e.path.startsWith(prefix));
  }, [scan, drill]);

  const typeBreakdown = useMemo(() => {
    const acc = new Map<string, { size: number; count: number }>();
    for (const e of scopedEntries) {
      if (e.name === '.keep') continue;
      const cat = typeCategory(e.contentType, e.name);
      const cur = acc.get(cat) || { size: 0, count: 0 };
      cur.size += e.size;
      cur.count += 1;
      acc.set(cat, cur);
    }
    return Array.from(acc.entries())
      .map(([label, v]) => ({ label, ...v }))
      .sort((a, b) => b.size - a.size);
  }, [scopedEntries]);

  const largest = useMemo<ScanEntry[]>(
    () => scopedEntries.filter((e) => e.name !== '.keep').sort((a, b) => b.size - a.size).slice(0, LARGEST_COUNT),
    [scopedEntries]
  );

  const chartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'treemap',
        toolbar: { show: false },
        events: {
          dataPointSelection: (_e: any, _ctx: any, cfg: any) => {
            const n = treemapNodesRef.current[cfg.dataPointIndex];
            if (n && n.children) setDrill(n.path);
          },
        },
      },
      legend: { show: false },
      colors: PALETTE,
      plotOptions: { treemap: { distributed: true, enableShades: false } },
      dataLabels: {
        enabled: true,
        style: { fontSize: '12px', fontWeight: 600 },
        formatter: ((text: any, op: any) => `${text} · ${fmtBytes(op.value)}`) as any,
      },
      tooltip: { y: { formatter: (v: number) => fmtBytes(v) } },
      states: { active: { filter: { type: 'none' } } },
    }),
    []
  );

  const chartSeries = useMemo(
    () => [{ data: treemapNodes.map((n) => ({ x: n.name + (n.children ? '/' : ''), y: n.size })) }],
    [treemapNodes]
  );

  // ---- per-file actions ----------------------------------------------------

  const openFile = async (e: ScanEntry) => {
    setBusyPath(e.path);
    try {
      const url = await getDownloadURL(ref(storage, e.path));
      window.open(url, '_blank', 'noopener');
    } catch (err: any) {
      toast.error(`Couldn't open: ${err?.message || err}`);
    } finally {
      setBusyPath('');
    }
  };

  const copyUrl = async (e: ScanEntry) => {
    setBusyPath(e.path);
    try {
      const url = await getDownloadURL(ref(storage, e.path));
      await navigator.clipboard.writeText(url);
      toast.success('Download URL copied');
    } catch (err: any) {
      toast.error(`Couldn't copy URL: ${err?.message || err}`);
    } finally {
      setBusyPath('');
    }
  };

  const deleteFile = async (e: ScanEntry) => {
    if (!window.confirm(`Delete "${e.path}"?\nThis cannot be undone.`)) return;
    setBusyPath(e.path);
    try {
      await deleteObject(ref(storage, e.path));
      if (cachedScan) {
        cachedScan = { ...cachedScan, entries: cachedScan.entries.filter((x) => x.path !== e.path) };
        setScan(cachedScan);
        persistScan(cachedScan);
      }
      toast.success(`Deleted ${e.name}`);
    } catch (err: any) {
      toast.error(`Delete failed: ${err?.message || err}`);
    } finally {
      setBusyPath('');
    }
  };

  // ---- render --------------------------------------------------------------

  const scanning = phase === 'listing' || phase === 'sizing';
  const crumbs = drill ? drill.split('/') : [];
  const parentPath = (p: string): string => p.split('/').slice(0, -1).join('/');

  return (
    <div className='card mb-5'>
      <div className='card-header border-0 pt-6'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Storage usage</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            {scan ? `Scanned ${fmtAgo(scan.at)} in ${(scan.durationMs / 1000).toFixed(1)}s` : "What's consuming the bucket"}
          </span>
        </h3>
        <div className='card-toolbar gap-2'>
          {scanning ? (
            <>
              <span className='text-muted fs-7'>
                {phase === 'listing' ? `Listing… ${foldersListed} folders · ${found} objects` : `Reading sizes… ${sized} / ${found}`}
              </span>
              <button type='button' className='btn btn-sm btn-light' onClick={cancelScan}>
                Cancel
              </button>
            </>
          ) : phase === 'restoring' ? (
            <span className='text-muted fs-7'>
              <span className='spinner-border spinner-border-sm me-2' />
              Loading saved scan…
            </span>
          ) : (
            <button type='button' className='btn btn-sm btn-light-primary' onClick={runScan}>
              <i className='bi bi-radar me-1' />
              {scan ? 'Rescan' : 'Analyze storage'}
            </button>
          )}
        </div>
      </div>

      <div className='card-body pt-2'>
        {phase === 'restoring' && (
          <div className='text-center py-10 text-muted'>
            <span className='spinner-border spinner-border-sm me-2' />
            Loading saved scan…
          </div>
        )}

        {phase === 'idle' && (
          <div className='text-center py-10'>
            <i className='bi bi-pie-chart fs-3x text-muted d-block mb-3' />
            <div className='text-muted mb-4'>
              Walks every folder and reads each object's size (one metadata request per object),
              then breaks down where the space goes.
            </div>
            <button type='button' className='btn btn-primary' onClick={runScan}>
              <i className='bi bi-radar me-1' />
              Analyze storage
            </button>
          </div>
        )}

        {scanning && (
          <div className='text-center py-10 text-muted'>
            <span className='spinner-border spinner-border-sm me-2' />
            {phase === 'listing'
              ? `Listing folders… ${foldersListed} listed · ${found} objects found so far`
              : `Reading sizes… ${sized} / ${found}`}
          </div>
        )}

        {phase === 'done' && scan && drillNode && (
          <>
            {/* stat strip */}
            <div className='d-flex flex-wrap gap-6 mb-4'>
              <div>
                <div className='fs-2 fw-bold'>{fmtBytes(drillNode.size)}</div>
                <div className='text-muted fs-8'>{drill ? `in ${drillNode.name}/` : 'total stored'}</div>
              </div>
              <div>
                <div className='fs-2 fw-bold'>{drillNode.count.toLocaleString()}</div>
                <div className='text-muted fs-8'>objects</div>
              </div>
              {scan.unreadable > 0 && (
                <div>
                  <div className='fs-2 fw-bold text-warning'>{scan.unreadable}</div>
                  <div className='text-muted fs-8'>unreadable</div>
                </div>
              )}
            </div>

            {/* drill breadcrumbs */}
            <div className='d-flex align-items-center flex-wrap mb-2 fs-7'>
              <button type='button' className='btn btn-link p-0 fw-semibold fs-7' onClick={() => setDrill('')} disabled={!drill}>
                <i className='bi bi-hdd me-1' />
                Storage
              </button>
              {crumbs.map((c, i) => {
                const target = crumbs.slice(0, i + 1).join('/');
                const last = i === crumbs.length - 1;
                return (
                  <React.Fragment key={target}>
                    <span className='text-muted mx-2'>/</span>
                    {last ? (
                      <span className='fw-bold'>{c}</span>
                    ) : (
                      <button type='button' className='btn btn-link p-0 fw-semibold fs-7' onClick={() => setDrill(target)}>
                        {c}
                      </button>
                    )}
                  </React.Fragment>
                );
              })}
              {drill && onOpenFolder && (
                <button type='button' className='btn btn-link p-0 fs-8 ms-4' onClick={() => onOpenFolder(drill)}>
                  <i className='bi bi-folder2-open me-1' />
                  open in file manager
                </button>
              )}
            </div>

            <div className='row g-5'>
              {/* treemap */}
              <div className='col-lg-8'>
                {treemapNodes.length > 0 ? (
                  <>
                    <Chart options={chartOptions} series={chartSeries} type='treemap' height={340} />
                    <div className='text-muted fs-8 mt-1'>Click a folder tile to drill in.</div>
                  </>
                ) : (
                  <div className='text-muted text-center py-10'>Nothing with a size in here.</div>
                )}
              </div>

              {/* by type */}
              <div className='col-lg-4'>
                <div className='fw-bold fs-6 mb-3'>By file type</div>
                {typeBreakdown.length === 0 && <div className='text-muted fs-7'>No files in scope.</div>}
                {typeBreakdown.map((t, i) => {
                  const pct = drillNode.size > 0 ? Math.round((t.size / drillNode.size) * 100) : 0;
                  return (
                    <div key={t.label} className='mb-3'>
                      <div className='d-flex justify-content-between fs-7 mb-1'>
                        <span className='fw-semibold'>{t.label}</span>
                        <span className='text-muted'>
                          {fmtBytes(t.size)} · {t.count.toLocaleString()}
                        </span>
                      </div>
                      <div className='progress' style={{ height: 6 }}>
                        <div className='progress-bar' style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: PALETTE[i % PALETTE.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* largest files */}
            <div className='fw-bold fs-6 mt-6 mb-2'>
              Largest files {drill ? `in ${drill}/` : ''}
            </div>
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-muted fs-8 text-uppercase'>
                    <th>File</th>
                    <th className='w-200px'>Folder</th>
                    <th className='w-90px'>Size</th>
                    <th className='w-150px'>Modified</th>
                    <th className='w-125px text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {largest.map((e) => {
                    const folder = parentPath(e.path);
                    const guarded = isProtected(e.path);
                    return (
                      <tr key={e.path}>
                        <td>
                          <button type='button' className='btn btn-link p-0 text-start fs-7' onClick={() => openFile(e)} title={e.path}>
                            <i className={`bi ${fileIcon(e.name, e.contentType)} me-2`} />
                            {e.name}
                          </button>
                        </td>
                        <td className='text-muted fs-8'>
                          {onOpenFolder ? (
                            <button type='button' className='btn btn-link p-0 fs-8 text-muted' onClick={() => onOpenFolder(folder)} title='Open in file manager'>
                              {folder || '(root)'}
                            </button>
                          ) : (
                            folder || '(root)'
                          )}
                        </td>
                        <td className='fs-7 fw-semibold'>{fmtBytes(e.size)}</td>
                        <td className='text-muted fs-8'>{fmtDate(e.updated)}</td>
                        <td className='text-end'>
                          <div className='d-flex justify-content-end gap-1'>
                            <button type='button' className='btn btn-icon btn-sm btn-light' onClick={() => copyUrl(e)} disabled={busyPath === e.path} title='Copy download URL'>
                              <i className='bi bi-link-45deg' />
                            </button>
                            {guarded ? (
                              <span className='badge badge-light-warning align-self-center' title='Referenced by app data — delete disabled'>
                                op
                              </span>
                            ) : (
                              <button type='button' className='btn btn-icon btn-sm btn-light-danger' onClick={() => deleteFile(e)} disabled={busyPath === e.path} title='Delete'>
                                {busyPath === e.path ? <span className='spinner-border spinner-border-sm' /> : <i className='bi bi-trash' />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {largest.length === 0 && (
                    <tr>
                      <td colSpan={5} className='text-muted text-center py-6'>
                        No files in scope.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StorageUsageCard;
